import { Col, Empty, Row, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { CreatorEfficiency, EfficiencyTopRow } from '../contract';
import { creatorName, fmtDec, fmtHours, fmtKc, fmtNum, pctChange } from '../format';
import { MetricRow, PctBadge } from './metric-row';
import { rowOpenProps } from './row-open';
import styles from '../style.module.scss';
import more from '../style-more.module.scss';

type EffKey = keyof CreatorEfficiency;

/** Nhãn + định dạng 5 chỉ số hiệu suất (đặc tả 06 E1). */
export const EFFICIENCY_ROWS: { key: EffKey; label: string; fmt: (v: number) => string }[] = [
  { key: 'kcPerHour', label: 'KC mỗi giờ LIVE', fmt: (v) => fmtKc(v) },
  { key: 'followersPerHour', label: 'Follower mới mỗi giờ', fmt: (v) => fmtDec(v) },
  { key: 'kcPerSession', label: 'KC mỗi phiên LIVE', fmt: (v) => fmtKc(v) },
  { key: 'hoursPerSession', label: 'Giờ mỗi phiên', fmt: (v) => fmtHours(v) },
  { key: 'kcPerPk', label: 'KC mỗi trận PK', fmt: (v) => fmtKc(v) },
];

/** Danh sách 5 chỉ số, mỗi dòng: số tháng này · tháng trước · % (khi có tháng trước). `cmpCurr` = tháng này
 *  của CÙNG tập Creator với `prev` (phạm vi: chỉ các Creator so được) — % tính trên tập đó. */
export const EfficiencyList = ({
  curr,
  prev,
  cmpCurr,
}: {
  curr: CreatorEfficiency | null | undefined;
  prev: CreatorEfficiency | null | undefined;
  cmpCurr?: CreatorEfficiency | null;
}) => (
  <>
    {EFFICIENCY_ROWS.map(({ key, label, fmt }) => {
      const c = curr?.[key] ?? null;
      const p = prev?.[key] ?? null;
      const cc = cmpCurr ? cmpCurr[key] ?? null : c;
      return (
        <MetricRow
          key={key}
          label={label}
          value={c === null ? '—' : fmt(c)}
          hint={
            p === null ? undefined : (
              <>
                {cmpCurr && cc !== null ? `${fmt(cc)} so ` : 'tháng trước '}
                {fmt(p)}
                {cmpCurr ? ' tháng trước' : ''} · <PctBadge pct={pctChange(cc, p)} />
              </>
            )
          }
        />
      );
    })}
  </>
);

type ScopeEfficiency = CreatorEfficiency & {
  prev: CreatorEfficiency | null;
  compared?: { curr: CreatorEfficiency; creators: number } | null;
};

interface Props {
  efficiency: ScopeEfficiency | null | undefined;
  top: EfficiencyTopRow[] | null | undefined;
  onSelect: (uid: string) => void;
  /** Số Creator của phạm vi — so tháng trước chỉ trên một phần thì ghi "trên N Creator". */
  scopeCreators?: number;
}

/** Khối "Hiệu suất": số của cả phạm vi (theo tổng) + Top 10 KC/giờ (Creator ≥ 10 giờ LIVE). */
const EfficiencySection = ({ efficiency, top, onSelect, scopeCreators }: Props) => {
  if (!efficiency && !top) return null;
  const cmp = efficiency?.compared ?? null;
  const subset = cmp && scopeCreators !== undefined && cmp.creators < scopeCreators;
  const columns: ColumnsType<EfficiencyTopRow> = [
    {
      title: 'Creator',
      key: 'creator',
      render: (_, r) => (
        <div className={styles.cellCreator}>
          <span className={styles.cellName}>{creatorName(r)}</span>
          {r.groupName && <span className={styles.cellGroup}>{r.groupName}</span>}
        </div>
      ),
    },
    {
      title: 'KC/giờ',
      dataIndex: 'kcPerHour',
      align: 'right',
      width: 100,
      render: (v: number) => <strong>{fmtKc(v)}</strong>,
    },
    {
      title: 'Giờ LIVE',
      dataIndex: 'liveHours',
      align: 'right',
      width: 100,
      render: (v: number) => fmtHours(v),
    },
    {
      title: 'So tháng trước',
      key: 'prev',
      width: 130,
      render: (_, r) =>
        r.prevKcPerHour === null ? (
          <span className={styles.muted}>—</span>
        ) : (
          <span title={`Tháng trước ${fmtKc(r.prevKcPerHour)} KC/giờ`}>
            <PctBadge pct={pctChange(r.kcPerHour, r.prevKcPerHour)} />
          </span>
        ),
    },
  ];
  return (
    <section className={`${styles.card} ${styles.section}`} aria-label="Hiệu suất">
      <h2 className={styles.cardTitle}>Hiệu suất</h2>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={9}>
          <EfficiencyList
            curr={efficiency}
            prev={efficiency?.prev}
            cmpCurr={subset ? cmp?.curr : null}
          />
          <p className={more.note}>
            Tính theo tổng của phạm vi (không phải trung bình từng Creator).
            {subset && cmp
              ? ` So tháng trước trên ${fmtNum(cmp.creators)} Creator có số tháng trước.`
              : ''}
          </p>
        </Col>
        <Col xs={24} lg={15}>
          <h3 className={more.subTitle}>Top 10 KC mỗi giờ LIVE (Creator từ 10 giờ LIVE)</h3>
          {top && top.length ? (
            <Table<EfficiencyTopRow>
              size="small"
              rowKey="uid"
              columns={columns}
              dataSource={top}
              pagination={false}
              scroll={{ x: 460 }}
              rowClassName={() => styles.clickRow}
              onRow={(r) => rowOpenProps(`Xem chi tiết ${creatorName(r)}`, () => onSelect(r.uid))}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Chưa có Creator đủ 10 giờ LIVE."
            />
          )}
        </Col>
      </Row>
    </section>
  );
};

export default EfficiencySection;
